import { useEffect, useMemo, useState } from "react";
import ButtonIconH from "./ButtonIconH";
import Dropdown from "./Dropdown";
import CheckBox from "./CheckBox";
import { familiesList, formatValueUnit, userData } from "../js/utils";
import { post } from "../js/post";
import { TabSelector } from "./TabSelector";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Bar,
  BarChart,
  Rectangle,
} from "recharts";
import {
  axisStyle,
  axisXLabel,
  axisYLabel,
  barChartMargin,
  barColors,
  legendStyle,
  titleStyle,
} from "../js/barChartStyle";
import { useOptics } from "../hooks/useOptics";
import { useSuppliers } from "../hooks/useSuppliers";
import { useBrands } from "../hooks/useBrands";

const CHART_TABS = ["Familias", "Proveedores", "Marcas"];

// Pestaña "Gráficos" de Dashboard / Almacén
// Pestaña "Gráficos" de Dashboard / Almacén
export function DashboardStoreChartsPanel() {
  const [chartTabSelected, setChartTabSelected] = useState("Familias");
  const opticalList = useOptics();

  return (
    <div>
      <TabSelector
        options={CHART_TABS}
        selected={chartTabSelected}
        style={{ borderBottom: "none" }}
        onChange={(e) => {
          setChartTabSelected(e);
        }}
      />
      {/* Las tres pestañas permanecen montadas para conservar su estado */}
      {CHART_TABS.map((tab) => (
        <div
          key={tab}
          style={{ display: chartTabSelected === tab ? "block" : "none" }}
        >
          <DashboardStoreChartsTab tab={tab} opticalList={opticalList} />
        </div>
      ))}
    </div>
  );
}

// Gráfico de una categoría (Familias, Proveedores o Marcas)
function DashboardStoreChartsTab({ tab, opticalList }) {
  const [selectedFamily, setSelectedFamily] = useState({ id: "", text: "" });
  const [rawSupplierList] = useSuppliers(selectedFamily.id);
  // Todos los desplegables empiezan con una opción vacía seleccionable
  const familyOptions = useMemo(
    () => [{ id: "", text: "" }, ...familiesList],
    [],
  );
  const opticalOptions = useMemo(
    () => [{ id: "", text: "" }, ...opticalList],
    [opticalList],
  );
  const supplierOptions = useMemo(
    () => [
      { id: "", text: "" },
      ...rawSupplierList.map((e) => ({ id: e.id, text: e.nombre })),
    ],
    [rawSupplierList],
  );
  const [selectedSupplier, setSelectedSupplier] = useState({
    id: "",
    text: "",
  });
  const [rawBrandList] = useBrands(selectedFamily.id, selectedSupplier.id, "");
  const brandList = useMemo(() => {
    const brands = rawBrandList.map((e) => ({
      id: e,
      text: e,
    }));
    return [{ id: "", text: "" }, ...brands];
  }, [rawBrandList]);
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedOptical, setSelectedOptical] = useState({ id: "", text: "" });
  const [showGlobal, setShowGlobal] = useState(false);
  const [selectedYearView, setSelectedYearView] = useState("5 Años");
  const [data, setData] = useState([]);
  const [selectedOption, setSelectedOption] = useState({
    volumenVentas: true,
    dto: false,
    ventasNetas: false,
    comprasNetas: false,
    udsCompradas: false,
    udsVendidas: false,
    mcc: false,
    margen: false,
    mcv: false,
  });

  function handleActiveOption(key) {
    setSelectedOption((prev) =>
      Object.keys(prev).reduce((acc, curr) => {
        acc[curr] = curr === key;
        return acc;
      }, {}),
    );
  }

  const getActiveOptionName = () => {
    const nameMap = {
      volumenVentas: "Volumen de ventas",
      dto: "DTO",
      ventasNetas: "Ventas netas",
      comprasNetas: "Compras netas",
      udsCompradas: "Unidades compradas",
      udsVendidas: "Unidades vendidas",
      mcc: "MCC",
      margen: "Margen",
      mcv: "MCV",
    };

    const activeKey = Object.keys(selectedOption).find(
      (key) => selectedOption[key],
    );
    return nameMap[activeKey];
  };

  const customTooltip = ({ payload, label, active }) => {
    if (!active || !payload || !payload.length) return null;

    const valorData = payload.find((item) => item.dataKey === "valor");
    const valorGlobalData = payload.find(
      (item) => item.dataKey === "valorGlobal",
    );

    return (
      <div
        className="recharts-default-tooltip"
        style={{
          margin: "0px",
          padding: "10px",
          backgroundColor: "rgb(255, 255, 255)",
          border: "1px solid rgb(204, 204, 204)",
          whiteSpace: "nowrap",
          borderRadius: "4px",
          boxShadow: "rgba(0, 0, 0, 0.1) 0px 2px 8px",
        }}
      >
        <p className="recharts-tooltip-label">{`Año: ${label}`}</p>
        <ul
          className="recharts-tooltip-item-list"
          style={{
            padding: "0px",
            margin: "0px",
            listStyleType: "none",
          }}
        >
          <li
            className="recharts-tooltip-item"
            style={{
              color: barColors.blue,
            }}
          >
            <span className="recharts-tooltip-item-value">
              {`${getActiveOptionName()}: ${formatValueUnit(
                valorData?.value || 0,
                getFormatUnit(),
              )}`}
            </span>
          </li>
          {showGlobal && valorGlobalData && (
            <li
              className="recharts-tooltip-item"
              style={{
                color: barColors.orange,
              }}
            >
              <span className="recharts-tooltip-item-value">
                {`Valor global: ${formatValueUnit(
                  valorGlobalData.value,
                  getFormatUnit(),
                )}`}
              </span>
            </li>
          )}
        </ul>
      </div>
    );
  };

  const getFormatUnit = () => {
    const unitMap = {
      volumenVentas: "€",
      dto: "%",
      ventasNetas: "€",
      comprasNetas: "€",
      udsCompradas: "",
      udsVendidas: "",
      mcc: "%",
      margen: "€",
      mcv: "%",
    };

    const activeKey = Object.keys(selectedOption).find(
      (key) => selectedOption[key],
    );
    return unitMap[activeKey];
  };

  const getCategoryId = () => {
    const categoryMap = {
      Familias: 0,
      Proveedores: 1,
      Marcas: 2,
    };
    return categoryMap[tab];
  };

  const getViewId = () => {
    const viewMap = {
      "5 Años": 5,
      "10 Años": 10,
    };
    return viewMap[selectedYearView];
  };

  const getData = async () => {
    const response = await post(
      "support-extra/dashboard/ObtenerEstadisticasAlmacenGraficosDashboard.php",
      {
        token: userData.token,
        campoSeleccion: Object.keys(selectedOption).findIndex(
          (key) => selectedOption[key] === true,
        ),
        tipoCategoria: getCategoryId(),
        familia: selectedFamily.id,
        idOptica: selectedOptical.id,
        idProveedor: selectedSupplier.id,
        marca: selectedBrand,
        valorGlobal: showGlobal,
        ultimosAnos: getViewId(),
      },
    );
    setData(response);
  };

  useEffect(() => {
    getData();
  }, [
    selectedOptical.id,
    selectedSupplier.id,
    selectedBrand,
    showGlobal,
    selectedOption,
    selectedYearView,
  ]);

  useEffect(() => {
    setSelectedSupplier({ id: "", text: "" });
    if (selectedFamily.id === "") setSelectedOptical({ id: "", text: "" });
  }, [selectedFamily.id]);

  useEffect(() => {
    setSelectedBrand("");
  }, [selectedSupplier.id]);

  // Devuelve la línea de filtros a su estado inicial
  function clearFilters() {
    setSelectedFamily({ id: "", text: "" });
    setSelectedOptical({ id: "", text: "" });
    setSelectedSupplier({ id: "", text: "" });
    setSelectedBrand("");
    setShowGlobal(false);
  }

  return (
    <div
      className="thin-section-rounded-border"
      style={{ display: "flex", flexDirection: "column", gap: 20 }}
    >
      <div className="opt-table-content grid-7" id="dashboard-stats-popup-grid">
        <div className="cell cell-start-content">
          <div className="data-field w-100">
            <span className="label">Familia</span>
            <Dropdown
              optionList={familyOptions}
              size={0}
              op={selectedFamily.id}
              onChange={(e) => {
                setSelectedFamily(e);
              }}
            ></Dropdown>
          </div>
        </div>
        <div className="cell"></div>
        <div className="cell cell-start-content">
          <div className="data-field w-100">
            <span className="label">
              {tab === "Familias" ? "Óptica" : "Proveedor"}
            </span>
            {tab === "Familias" ? (
              <Dropdown
                optionList={opticalOptions}
                size={0}
                disabled={selectedFamily.id === ""}
                op={selectedOptical.id}
                onChange={(e) => {
                  setSelectedOptical(e);
                }}
              ></Dropdown>
            ) : (
              <Dropdown
                optionList={supplierOptions}
                size={0}
                disabled={selectedFamily.id === ""}
                op={selectedSupplier.id}
                onChange={(e) => {
                  setSelectedSupplier(e);
                }}
              ></Dropdown>
            )}
          </div>
        </div>
        <div className="cell"></div>
        <div className="cell cell-start-content">
          {tab === "Familias" && (
            <div className="data-field">
              <span className="label">Mostrar valor global</span>
              <CheckBox
                active={showGlobal}
                onClick={() => {
                  setShowGlobal(!showGlobal);
                }}
              ></CheckBox>
            </div>
          )}
          {tab === "Marcas" && (
            <div className="data-field w-100">
              <span className="label">Marca</span>
              <Dropdown
                optionList={brandList}
                size={0}
                disabled={selectedSupplier.id === ""}
                op={selectedBrand}
                onChange={(e) => {
                  setSelectedBrand(e.text);
                }}
              ></Dropdown>
            </div>
          )}
        </div>
        <div className="cell"></div>
        <div className="cell cell-end-content">
          <button className="cell-label" onClick={clearFilters} tabIndex={-1}>
            Limpiar filtros
          </button>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          gap: 10,
        }}
      >
        <ButtonIconH
          text="Volumen de ventas"
          selected={selectedOption.volumenVentas}
          onClick={() => {
            handleActiveOption("volumenVentas");
          }}
        ></ButtonIconH>
        <ButtonIconH
          text="DTO %"
          selected={selectedOption.dto}
          onClick={() => {
            handleActiveOption("dto");
          }}
        ></ButtonIconH>
        <ButtonIconH
          text="Ventas netas"
          selected={selectedOption.ventasNetas}
          onClick={() => {
            handleActiveOption("ventasNetas");
          }}
        ></ButtonIconH>
        <ButtonIconH
          text="Compras netas"
          selected={selectedOption.comprasNetas}
          onClick={() => {
            handleActiveOption("comprasNetas");
          }}
        ></ButtonIconH>
        <ButtonIconH
          text="Unidades compradas"
          selected={selectedOption.udsCompradas}
          onClick={() => {
            handleActiveOption("udsCompradas");
          }}
        ></ButtonIconH>
        <ButtonIconH
          text="Unidades vendidas"
          selected={selectedOption.udsVendidas}
          onClick={() => {
            handleActiveOption("udsVendidas");
          }}
        ></ButtonIconH>
        <ButtonIconH
          text="MCC %"
          selected={selectedOption.mcc}
          onClick={() => {
            handleActiveOption("mcc");
          }}
        ></ButtonIconH>
        <ButtonIconH
          text="Margen €"
          selected={selectedOption.margen}
          onClick={() => {
            handleActiveOption("margen");
          }}
        ></ButtonIconH>
        <ButtonIconH
          text="MCV %"
          selected={selectedOption.mcv}
          onClick={() => {
            handleActiveOption("mcv");
          }}
        ></ButtonIconH>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        <TabSelector
          options={["5 Años", "10 Años"]}
          selected={selectedYearView}
          onChange={(e) => {
            setSelectedYearView(e);
          }}
        />
        <div style={{ width: "100%", height: "550px" }}>
          <div style={titleStyle}>Datos {getActiveOptionName()}</div>
          <ResponsiveContainer
            width="100%"
            height={500}
            style={{
              maxWidth: 1000,
              margin: "0 auto",
            }}
          >
            <BarChart
              width={500}
              height={300}
              data={data}
              margin={barChartMargin}
              barCategoryGap={5}
              barGap={0}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="ano" tick={axisStyle} label={axisXLabel("Año")} />
              <YAxis
                tick={axisStyle}
                label={axisYLabel(getActiveOptionName())}
              />
              <Tooltip content={customTooltip} />
              <Legend
                layout="horizontal"
                align="left"
                verticalAlign="top"
                wrapperStyle={legendStyle}
              />
              <Bar
                dataKey="valor"
                name={getActiveOptionName()}
                fill={barColors.blue}
                activeBar={
                  <Rectangle fill={barColors.blue} stroke={barColors.gray} />
                }
              />
              <Bar
                hide={!showGlobal}
                dataKey="valorGlobal"
                name="Valor global"
                fill={barColors.orange}
                activeBar={
                  <Rectangle fill={barColors.orange} stroke={barColors.gray} />
                }
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <ButtonIconH
          text={"Exportar"}
          small={true}
          onClick={() => {}}
        ></ButtonIconH>
      </div>
    </div>
  );
}
